/*
 * EwTrack (TN-01): the one way Everwow code pushes an event to GTM's dataLayer, plus the
 * capture of our own JS errors as `ew_js_error`.
 *
 * - push(event, params): refuses names that do not start with `ew_`; flushes every
 *   parameter of the shared registry (a GTM data-model key stays set for the rest of the
 *   page otherwise, so an `ew_nav_click` would carry the `status` of an earlier
 *   `ew_email_result`); never throws. No `qa` parameter: QA traffic is `html[data-ew-qa]`
 *   (layout/theme.liquid, from the `_ew_qa` cart attribute) or a `preview_theme_id` URL,
 *   which GTM maps to `traffic_type=internal`.
 * - device(): 'phone' (< 750), 'tablet' (750–989) or 'desktop' (≥ 990).
 * - `ew_js_error {err_file, err_msg}` for uncaught errors and rejections from our own files
 *   only (ew-*.js, cart, header, email-popup, theme), the message cut to 100 characters with
 *   anything email-shaped masked, at most 3 per page view. The listeners never call
 *   preventDefault(), so the console still shows the error.
 *
 * - The pushes in CLARITY_EVENTS also reach Microsoft Clarity, as an API event plus a custom
 *   tag named after the event whose value is the first CLARITY_TAG_KEYS parameter the push
 *   carries, so a recording can be filtered by what was picked, not only that something was.
 *   Clarity is called on the next task, never inside the tap that pushed the event, and only
 *   when `window.clarity` exists.
 *
 * Loaded first of the deferred scripts (straight after GTM in layout/theme.liquid). There is
 * no fallback anywhere: every caller guards with `window.EwTrack && EwTrack.push(…)`.
 */
(function () {
  // The one registry of parameter keys, shared with GTM's Data Layer Variables (RA-10).
  const REGISTRY = [
    'item',
    'level',
    'device',
    'place',
    'trigger',
    'offer',
    'status',
    'action',
    'ordinal',
    'product_id',
    'addon_id',
    'step',
    'currency',
    'on',
    'pair',
    'how',
    'problem',
    'reason',
    'err_file',
    'err_msg',
    'pdp_test',
    'pdp_variant',
  ];
  const CLARITY_EVENTS = [
    'ew_offer_apply',
    'ew_offer_remove',
    'ew_offer_view',
    'ew_idle_sheet_open',
    'ew_idle_sheet_action',
    'ew_email_submit',
    'ew_email_result',
    'ew_strip_add',
    'ew_strip_view',
    'ew_addon_on',
    'ew_addon_off',
    'ew_greeting_preview_open',
    'ew_greeting_preview_close',
    'ew_iconic_toggle',
    'ew_discount_mismatch',
    'ew_search_open',
    'ew_search_result_click',
    'ew_search_no_results',
  ];
  // Short, enumerated values only. Never `item` (search text), `err_msg`, `err_file`,
  // `problem` or `reason`: they can carry free text, and a tag value is stored as sent.
  const CLARITY_TAG_KEYS = [
    'offer',
    'addon_id',
    'action',
    'status',
    'how',
    'trigger',
    'place',
    'on',
    'step',
    'level',
    'ordinal',
  ];
  const CLARITY_TAG_MAX = 60;
  const OWN_FILE =
    /\/assets\/(ew-[^/?#\s)]+|cart|header|email-popup|theme)\.js/;
  const EMAIL_SHAPED = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
  const MAX_ERRORS = 3;
  const MAX_MESSAGE = 100;

  let errorsSent = 0;

  function build(event, params) {
    const entry = { event: event };
    REGISTRY.forEach(function (key) {
      entry[key] = undefined;
    });
    Object.keys(params || {}).forEach(function (key) {
      entry[key] = params[key];
    });
    return entry;
  }

  function push(event, params) {
    try {
      if (typeof event !== 'string' || event.indexOf('ew_') !== 0) return false;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(build(event, params));
      toClarity(event, params);
      return true;
    } catch (error) {
      return false;
    }
  }

  // The first tag key the push carries, as a string cut to CLARITY_TAG_MAX; null when none.
  function clarityTag(params) {
    const p = params || {};
    for (let i = 0; i < CLARITY_TAG_KEYS.length; i++) {
      const value = p[CLARITY_TAG_KEYS[i]];
      if (value !== undefined && value !== null && value !== '') {
        return String(value).slice(0, CLARITY_TAG_MAX);
      }
    }
    return null;
  }

  function toClarity(event, params) {
    try {
      if (CLARITY_EVENTS.indexOf(event) === -1) return;
      const tag = clarityTag(params);
      setTimeout(function () {
        try {
          if (typeof window.clarity !== 'function') return;
          window.clarity('event', event);
          if (tag) window.clarity('set', event, tag);
        } catch (error) {
          // A Clarity failure never reaches the page.
        }
      }, 0);
    } catch (error) {
      // Nor a scheduling one: the dataLayer push already happened.
    }
  }

  function device() {
    try {
      if (window.matchMedia('(min-width: 990px)').matches) return 'desktop';
      if (window.matchMedia('(min-width: 750px)').matches) return 'tablet';
    } catch (error) {
      // No matchMedia: the phone layout is the default one.
    }
    return 'phone';
  }

  // Emails are masked before the cut, so half an address can never survive it.
  function scrub(message) {
    return String(message == null ? '' : message)
      .replace(EMAIL_SHAPED, '[email]')
      .slice(0, MAX_MESSAGE);
  }

  // The basename of the first of our own asset URLs in `text` (a filename or a stack).
  function ownFile(text) {
    const match = OWN_FILE.exec(String(text || ''));
    return match ? match[1] + '.js' : null;
  }

  function report(file, message) {
    if (!file || errorsSent >= MAX_ERRORS) return false;
    errorsSent += 1;
    return push('ew_js_error', { err_file: file, err_msg: scrub(message) });
  }

  function onError(event) {
    try {
      const error = event && event.error;
      report(
        ownFile(event && event.filename) || ownFile(error && error.stack),
        (event && event.message) || (error && error.message),
      );
    } catch (ignored) {
      // Never let the reporter throw.
    }
  }

  function onRejection(event) {
    try {
      const reason = event && event.reason;
      report(
        ownFile(reason && reason.stack),
        reason && reason.message ? reason.message : reason,
      );
    } catch (ignored) {
      // Never let the reporter throw.
    }
  }

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
  }

  window.EwTrack = {
    push: push,
    device: device,
    _test: {
      REGISTRY: REGISTRY,
      CLARITY_EVENTS: CLARITY_EVENTS,
      CLARITY_TAG_KEYS: CLARITY_TAG_KEYS,
      clarityTag: clarityTag,
      build: build,
      scrub: scrub,
      ownFile: ownFile,
      onError: onError,
      onRejection: onRejection,
    },
  };
})();
